/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ITextFieldStyleProps, ITextFieldStyles } from '@fluentui/react/lib/TextField';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { useFormState } from 'src/core/hooks/useFormState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { FormValidators } from 'src/core/utils/formValidators';
import { EntityRole } from 'src/luis/projects/entities/types/EntityRole';
import { LuisId } from 'src/luis/types/Resource';

type EditableTextProps = {
    /**
     * The child id.
     */
    childId: LuisId;

    /**
     * The entity child name.
     */
    childName: string;

    /**
     * A callback for when child name is changed.
     */
    onChange: (id: LuisId, text: string) => void;
};

const messages = defineMessages({
    fieldPlaceholder: {
        id: 'entities.hierarchicalEntityInputField.fieldPlaceholder',
        defaultMessage: 'Type child name ...'
    }
});

const getClassName = classNamesFunction<ITextFieldStyleProps, ITextFieldStyles>();

const fabricTransparentFieldClassName = getClassName({ field: { padding: 0 }, fieldGroup: { backgroundColor: 'transparent' } });

export const HierarchicalEntityInputField = (props: EditableTextProps) => {
    const { childId, childName, onChange } = props;
    const { localize } = useLocalization();
    const { formState, updateFormState, formStateDto } = useFormState<Pick<EntityRole, 'name'>>({
        name: [
            childName,
            FormValidators.createRequiredValidator(),
            FormValidators.createDuplicateValueValidator(childName),
            FormValidators.createBlacklistValidator()
        ]
    });

    /**
     * Submits the new role name when the form is in
     * a valid state.
     */
    const onSubmit = () => {
        if (formState.form.valid) {
            onChange(childId, formStateDto.name);
        }
    };

    return (
        <LuisTextField
            borderless
            styles={fabricTransparentFieldClassName}
            placeholder={localize(messages.fieldPlaceholder)}
            value={formState.fields.name.value}
            onChange={(_, name) => updateFormState({ name })}
            onEnterKeyUp={onSubmit}
            onBlur={onSubmit}
        />
    );
};
